import { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import { setCookie, getCookie } from '@utils/cookies';

const ThemeContext = createContext(null);

function systemPrefersDark() {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
}

function resolveDark(mode) {
  if (mode === 'system') return systemPrefersDark();
  return mode === 'dark';
}

export const ThemeProvider = ({ children }) => {
  const [mode, setModeState] = useState(() => {
    const saved = getCookie('pf_theme');
    if (saved === 'dark' || saved === 'light' || saved === 'system') return saved;
    if (saved === 'dark') return 'dark';
    return 'light';
  });

  const dark = useMemo(() => resolveDark(mode), [mode]);

  useEffect(() => {
    const root = document.documentElement;
    if (dark) root.classList.add('dark');
    else root.classList.remove('dark');
    setCookie('pf_theme', mode, 365);
  }, [dark, mode]);

  useEffect(() => {
    if (mode !== 'system') return undefined;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => {
      const root = document.documentElement;
      if (mq.matches) root.classList.add('dark');
      else root.classList.remove('dark');
    };
    mq.addEventListener?.('change', onChange);
    return () => mq.removeEventListener?.('change', onChange);
  }, [mode]);

  const setMode = useCallback((next) => {
    setModeState(next);
  }, []);

  const toggleTheme = useCallback(() => {
    setModeState((prev) => {
      const currentlyDark = resolveDark(prev);
      return currentlyDark ? 'light' : 'dark';
    });
  }, []);

  return (
    <ThemeContext.Provider value={{ dark, mode, setMode, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) throw new Error('useTheme must be used within ThemeProvider');
  return context;
};
